import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, AlertTriangle, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PAYROLL_SETTINGS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

/**
 * Payroll settings — single-page view of every rule the calc pipeline
 * consults. Each rule shows its current value and a Confirmed / Awaiting
 * client-answer badge so Michael can see at a glance what's assumed.
 *
 * Defaults are deliberately conservative (clamp OFF, rounding 0, break 0)
 * so nothing is silently guessed while Ben chases the authoritative rules.
 */

const RULE_SPECS = [
    {
        section: "Rostered start clamp",
        note: "Late-arrival policy. When ON and an employee's roster is set, a badge at 08:22 for an 08:30 roster pays from 08:30. Neither the flag nor the times can be turned on until the client confirms.",
        keys: [
            { key: "rostered_clamp_enabled", type: "bool", label: "Clamp enabled" },
            { key: "default_rostered_start", type: "time", label: "Default rostered start (HH:MM)" },
            { key: "default_rostered_finish", type: "time", label: "Default rostered finish (HH:MM)" },
        ],
    },
    {
        section: "Early clock-in",
        note: "Clock-ins before the normal start are paid from the normal start, unless an admin ticks “Authorised early start” on that day's Window exception row in the pay run. The normal-day window also drives the Window exceptions strip.",
        keys: [
            { key: "normal_start", type: "time", label: "Normal start (HH:MM)" },
            { key: "normal_finish", type: "time", label: "Normal finish (HH:MM)" },
            { key: "pay_before_normal_start", type: "bool", label: "Pay time before normal start" },
            { key: "clamp_applies_weekends", type: "bool", label: "Clamp also applies Sat/Sun" },
        ],
    },
    {
        section: "Rounding",
        note: "Applied after the early-start clamp, before lunch. in_up_out_down rounds each clock-in UP and each clock-out DOWN to the increment. The setting does exactly what it shows — the Confirmed tick is a badge only.",
        keys: [
            { key: "rounding_increment_minutes", type: "int", label: "Increment (minutes)", options: [0, 5, 10, 15, 30] },
            { key: "rounding_direction", type: "select", label: "Direction", options: ["in_up_out_down", "nearest", "up", "down"] },
        ],
    },
    {
        section: "Lunch",
        note: "Unpaid lunch deducted from the rounded clock span on every working day that reaches the minimum worked hours. Overtime thresholds (8 h / 12 h) apply to the paid hours after lunch.",
        keys: [
            { key: "lunch_deduct_from_biometric", type: "bool", label: "Deduct unpaid lunch" },
            { key: "lunch_break_minutes", type: "int", label: "Lunch length (minutes)" },
            { key: "lunch_deduct_min_worked_hours", type: "int", label: "Only deduct on days of at least (hours)" },
        ],
    },
    {
        section: "Unpaid break",
        note: "Deducted from the paid span. 'always' subtracts every day; 'over_hours' only when the day exceeds the threshold; 'flagged' waits for a per-day break-taken flag that the biometric file doesn't currently carry.",
        keys: [
            { key: "unpaid_break_minutes", type: "int", label: "Break length (minutes)" },
            { key: "unpaid_break_condition", type: "select", label: "Condition", options: ["always", "over_hours", "flagged"] },
            { key: "unpaid_break_threshold_hours", type: "int", label: "Threshold hours (for 'over_hours')" },
        ],
    },
    {
        section: "Overtime rules",
        note: "Locked with the client and cannot be edited from here. Displayed as a reminder of what the day-of-week split applies.",
        keys: [
            { key: "overtime_rules", type: "readonly-obj", label: "Bucket splits" },
        ],
    },
    {
        section: "Period length",
        note: "The expected row count for a biometric import is derived from the pay period. This is the fallback when a period record has no explicit start_date.",
        keys: [
            { key: "fortnight_days", type: "int", label: "Days per pay period" },
        ],
    },
    {
        section: "Staff loan caps",
        note: "Ceiling on how much can be recovered from a single fortnight's net pay. Ben is chasing the PNG Labour Act limit. Until it's set + confirmed the app enforces no cap, but warns whenever a repayment exceeds half of net as a sanity check.",
        keys: [
            { key: "loan_max_kina_per_period", type: "int", label: "Max repayment (kina/fortnight)" },
            { key: "loan_max_pct_of_net_per_period", type: "int", label: "Max repayment (% of net)" },
        ],
    },
    {
        section: "Leave entitlement",
        note: (
            <>
                Whether PNG staff accrue annual and sick leave <strong>is unconfirmed</strong>. The
                accrual engine is built and running to the rates supplied 4 September 2026, but
                entitlement itself has not been confirmed in writing — the client's director has
                said in writing that PNG staff have no leave entitlements, contradicting the
                bookkeeper who supplied the accrual rates. Waiting on a written answer resolving
                the conflict.
            </>
        ),
        keys: [
            { key: "leave_entitlement", type: "readonly-note",
              label: "PNG staff annual + sick leave entitlement",
              awaitingText: "The rates the engine uses are AL 0.076923/hr and SL 0.034615/hr per qualifying hour, supplied 4 Sept 2026. This entry is intentionally an open question, not a value — see the section note above." },
        ],
    },
    {
        section: "Employer identification (for IRC forms)",
        note: (
            <>
                Header fields printed on IRC Forms S1, S2, and S6. If any field is missing the
                form still <em>downloads</em> so numbers can be spot-checked — but wears an
                <strong> INCOMPLETE — do not lodge</strong> watermark and shows a red <em>Missing
                — set in Settings</em> cell in place of the value. Fill these in before the first
                monthly lodgement.
            </>
        ),
        keys: [
            { key: "employer_name",    type: "text", label: "Employer name (as registered with IRC)" },
            { key: "employer_tin",     type: "text", label: "Tax Identification Number (TIN)" },
            { key: "employer_address", type: "text", label: "Employer address (single line)" },
            { key: "s1_travel_column", type: "select", label: "S1 travel column (2 = taxable allowance / 3 = taxable benefit)", options: [2, 3] },
        ],
    },
    {
        section: "Westpac PNG Direct Entry file",
        note: (
            <>
                The <strong>upload file</strong> Michael sends to Westpac Corporate Online. Every
                one of these fields is Westpac-issued or KP-side and MUST be confirmed before the
                first live upload. The generator refuses to emit a file naming the specific
                missing setting; it never falls back to Westpac's sample-file values.
                Description defaults to <code>PAYROLL</code> but stays unconfirmed because
                Westpac's own sample used <code>PAYMENTS</code> — pick one deliberately.
            </>
        ),
        keys: [
            { key: "westpac_user_supplying_name", type: "text", label: "User supplying the file (26 chars)" },
            { key: "westpac_user_id", type: "text", label: "Westpac User ID (exactly 6 digits)" },
            { key: "westpac_description", type: "text", label: "Description (12 chars, PAYROLL or PAYMENTS)" },
            { key: "westpac_debit_bsb", type: "text", label: "KP debit BSB (NNN-NNN)" },
            { key: "westpac_debit_account", type: "text", label: "KP debit account number (digits only, ≤ 15)" },
            { key: "westpac_debit_account_title", type: "text", label: "KP debit account title (32 chars)" },
            { key: "westpac_remitter_name", type: "text", label: "Remitter name (16 chars)" },
        ],
    },
];


function fmtValue(spec, v) {
    if (v === null || v === undefined || v === "") return "—";
    if (spec.type === "bool") return v ? "On" : "Off";
    if (spec.type === "readonly-obj") return JSON.stringify(v);
    return String(v);
}


function Badge({ confirmed }) {
    return confirmed ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
              style={{ background: "#E5F3EA", color: "var(--kp-success)" }}>
            <ShieldCheck size={10} /> Confirmed
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-display font-semibold uppercase tracking-[0.14em] rounded-[2px]"
              style={{ background: "#FFF3D6", color: "#B8721A" }}>
            <AlertTriangle size={10} /> Awaiting client answer
        </span>
    );
}


export default function PayrollSettings() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [settings, setSettings] = useState(null);
    const [dirty, setDirty] = useState({}); // { key: {value, confirmed} }
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/payroll/settings");
            setSettings(data);
            setDirty({});
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail));
        }
    };
    useEffect(() => { load(); }, []);

    if (!settings) return <div className="p-10 text-sm text-neutral-500">Loading settings…</div>;

    const merged = (key) => {
        const base = settings[key] || { value: null, confirmed: false };
        const patch = dirty[key] || {};
        return { ...base, ...patch };
    };
    const patch = (key, part) => setDirty((d) => ({ ...d, [key]: { ...(d[key] || {}), ...part } }));

    const saveAll = async () => {
        if (Object.keys(dirty).length === 0) return;
        setSaving(true);
        try {
            const { data } = await api.patch("/payroll/settings", { updates: dirty });
            setSettings(data);
            setDirty({});
            toast.success("Payroll rules updated");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid={PAYROLL_SETTINGS.page} className="p-10 max-w-[1000px]">
            <div className="mb-8">
                <div className="text-[11px] uppercase tracking-[0.24em] text-neutral-500 font-display">Settings · Payroll rules</div>
                <h1 className="font-display font-bold text-4xl mt-1 text-[color:var(--kp-charcoal)]">
                    Every rule the calculator consults
                </h1>
                <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-[700px]">
                    Each rule shows its current value and whether the client has confirmed it. Anything
                    marked <em>awaiting</em> is treated conservatively (clamp off, no rounding, no break)
                    so nothing gets silently guessed while we wait on answers.
                </p>
                {!isAdmin && (
                    <div
                        data-testid="payroll-readonly-chip"
                        className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-display font-semibold uppercase tracking-[0.14em] rounded-[3px]"
                        style={{ background: "#F0F0F1", color: "var(--kp-charcoal)", borderLeft: "3px solid #C88A17" }}
                    >
                        <ShieldCheck size={12} style={{ color: "#C88A17" }} />
                        Read-only · an admin can change these
                    </div>
                )}
            </div>

            {RULE_SPECS.map((section) => (
                <div key={section.section} className="mb-8 bg-white border border-neutral-200 rounded-[3px]">
                    <div className="px-5 py-4 border-b border-neutral-100">
                        <div className="text-[13px] font-display font-semibold text-[color:var(--kp-charcoal)]">
                            {section.section}
                        </div>
                        <div className="text-[12px] text-neutral-500 mt-1 leading-relaxed">
                            {section.note}
                        </div>
                    </div>
                    <div>
                        {section.keys.map((spec) => {
                            const s = merged(spec.key);
                            const readonly = spec.type === "readonly-obj" || !isAdmin;
                            return (
                                <div key={spec.key}
                                     data-testid={PAYROLL_SETTINGS.row(spec.key)}
                                     className="px-5 py-3 border-b border-neutral-100 last:border-b-0 grid grid-cols-[1fr_auto_auto] items-center gap-4">
                                    <div>
                                        <div className="text-[13px] text-[color:var(--kp-charcoal)]">{spec.label}</div>
                                        <div className="text-[11px] text-neutral-400 font-mono-money mt-0.5">{spec.key}</div>
                                    </div>
                                    <div className="min-w-[220px]">
                                        {spec.type === "bool" ? (
                                            <select
                                                data-testid={PAYROLL_SETTINGS.valueInput(spec.key)}
                                                disabled={readonly}
                                                value={s.value ? "true" : "false"}
                                                onChange={(e) => patch(spec.key, { value: e.target.value === "true" })}
                                                className="kp-focus w-full border border-neutral-300 px-3 py-1.5 text-sm bg-white rounded-[3px]"
                                            >
                                                <option value="false">Off</option>
                                                <option value="true">On</option>
                                            </select>
                                        ) : spec.type === "select" ? (
                                            <select
                                                data-testid={PAYROLL_SETTINGS.valueInput(spec.key)}
                                                disabled={readonly}
                                                value={s.value ?? spec.options[0]}
                                                onChange={(e) => patch(spec.key, { value: e.target.value })}
                                                className="kp-focus w-full border border-neutral-300 px-3 py-1.5 text-sm bg-white rounded-[3px]"
                                            >
                                                {spec.options.map((o) => <option key={o} value={o}>{o}</option>)}
                                            </select>
                                        ) : spec.type === "readonly-obj" ? (
                                            <pre className="text-[11px] text-neutral-600 bg-neutral-50 border border-neutral-200 px-3 py-2 rounded-[2px] font-mono-money whitespace-pre-wrap">
{JSON.stringify(s.value, null, 2)}
                                            </pre>
                                        ) : spec.type === "readonly-note" ? (
                                            <div
                                                data-testid={PAYROLL_SETTINGS.valueInput(spec.key)}
                                                className="text-[12px] text-neutral-600 bg-neutral-50 border border-neutral-200 px-3 py-2 rounded-[2px] leading-relaxed max-w-[400px]"
                                            >
                                                {spec.awaitingText}
                                            </div>
                                        ) : (
                                            <input
                                                data-testid={PAYROLL_SETTINGS.valueInput(spec.key)}
                                                type={spec.type === "int" ? "number" : "text"}
                                                disabled={readonly}
                                                value={s.value ?? ""}
                                                onChange={(e) => patch(spec.key, {
                                                    value: spec.type === "int" ? (e.target.valueAsNumber || 0) : e.target.value,
                                                })}
                                                placeholder={spec.type === "time" ? "HH:MM" : ""}
                                                className="kp-focus w-full border border-neutral-300 px-3 py-1.5 text-sm bg-white rounded-[3px] font-mono-money"
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Badge confirmed={s.confirmed} />
                                        {!readonly && spec.type !== "readonly-obj" && (
                                            <label className="inline-flex items-center gap-1 text-[11px] text-neutral-600 select-none">
                                                <input
                                                    data-testid={PAYROLL_SETTINGS.confirmedToggle(spec.key)}
                                                    type="checkbox"
                                                    checked={!!s.confirmed}
                                                    onChange={(e) => patch(spec.key, { confirmed: e.target.checked })}
                                                    className="accent-[color:var(--kp-success)]"
                                                />
                                                Confirmed
                                            </label>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {isAdmin && (
                <div className="sticky bottom-4 flex justify-end">
                    <button
                        data-testid={PAYROLL_SETTINGS.globalSaveBtn}
                        onClick={saveAll}
                        disabled={saving || Object.keys(dirty).length === 0}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] shadow-lg disabled:opacity-50"
                        style={{ background: "var(--kp-red)" }}
                    >
                        <Check size={14} /> {saving ? "Saving…" : `Save ${Object.keys(dirty).length} change(s)`}
                    </button>
                </div>
            )}
        </div>
    );
}
